import { useCallback, useState, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import moment from 'moment'
import { colors } from '@/styles'
import useProfile from '@/hooks/use-profile'
import { useGetCreditUsage } from '@/hooks/use-block-and-pay'
import useClickOutsideWithException from '@/hooks/use-click-outside-with-exception'
import Progressbar from '@/components/progressbar'
import Tooltip from '@/components/custom-tooltip'
import { showSampleModal } from '@/features/Application/sample-modal'
import { Report, House, CautionYellow, InfoCircleSmall } from '@/components/icons'
import useGetPlan from '@/hooks/use-get-plan'
import ENV_VARS from '@/config'
import Skeleton from '@/components/skeleton'
import {
  DropdownWrapper,
  SmallText,
  Line,
  Row,
  Indicators,
  SectionTitle,
  SectionContent,
  Col,
  IconWrapper,
  LargeText,
  LinkText,
  Content,
  HeaderItems,
  SecondaryText,
  Footer
} from './styles'
import { showWarning, getPercentage, tooltipMessage } from './utils'
const { WEB_URL } = ENV_VARS

type Props = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const TsDropdown = ({ isOpen, setIsOpen }: Props) => {
  const [isSampleOpen, setIsSampleOpen] = useState(false)
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.languages[0] === 'en'
  const data = useProfile()
  const { data: usage, isLoading: loadingCredit } = useGetCreditUsage()
  const currentPlan = data?.company_info?.base_plan_name ?? ''
  const close = () => {
    if (isSampleOpen) setIsSampleOpen(false)
    if (!isSampleOpen) setIsOpen(false)
  }

  const openSample = () => {
    setIsSampleOpen(true)
    showSampleModal({ reference: modalRef, cb: () => setIsSampleOpen(false) })
  }

  const modalRef = useRef(null)

  const ref = useClickOutsideWithException(close, modalRef)
  const { isLoading, data: planInfo } = useGetPlan()
  const billingDate = moment(planInfo?.nextResetDate).format(isEnglish ? 'MMMM Do' : 'MMM Do')

  const plans: any = {
    'essentials-plan-monthly': t('l.menu.view.essentials_free.button', 'Essentials (Free)'),
    'growth-plan-monthly': t('b.auth.create.growth.label', 'Growth'),
    'growth-plan-annual': t('b.auth.create.growth.label', 'Growth'),
    'business-plan-annual': t('b.auth.create.business.label', 'Business'),
    'business-plan-monthly': t('b.auth.create.business.label', 'Business'),
    'business-plan-special': t('b.auth.create.enterprise.label', 'Enterprise'),
    'growth-plan-monthly-coupon-wbrothers': t('b.auth.create.growth.label', 'Growth')
  }

  const usedCredits = usage?.blocks.credit_checks?.usage?.current_value
  const totalCredits = usage?.blocks.credit_checks?.value
  const planTitle = plans[currentPlan]

  const renderCaution = useCallback(() => {
    const caution = showWarning(usedCredits, totalCredits)

    if (caution) {
      return (
        <Tooltip
          text={tooltipMessage(getPercentage(usedCredits, totalCredits), currentPlan)}
          placement="top"
          offsetLeft={0}
          offsetRight={0}
        >
          <CautionYellow width={18} height={18} style={{ marginRight: '5px' }} />
        </Tooltip>
      )
    }
  }, [currentPlan, usedCredits])

  return (
    <DropdownWrapper ref={ref}>
      <Content>
        <HeaderItems>
          <LargeText>{t('l.plan.monthy_usage.text', 'Monthly Usage')}</LargeText>
          {isLoading ? (
            <Skeleton width={250} height={15} />
          ) : (
            <SmallText>
              {planTitle === undefined ? (
                <Trans i18nKey="l.unable_to_retrieve_plan.text">
                  Unable to retrieve plan. Please contact{' '}
                  <a href="mailto:support@liv.rent" style={{ color: colors.link }}>
                    support
                  </a>
                  .
                </Trans>
              ) : totalCredits === 0 ? (
                `${planTitle} ${t('b.plan.text', 'Plan')}`
              ) : (
                <Trans i18nKey="l.plan.billing_date.resets.text">
                  {{ planTitle }} Plan · Resets {{ billingDate }}
                </Trans>
              )}
            </SmallText>
          )}
        </HeaderItems>

        {/* Trust Score Reports */}
        {loadingCredit ? (
          <Skeleton width={350} height={60} />
        ) : (
          <Row>
            <IconWrapper>
              <Report width={16} height={16} />
            </IconWrapper>
            <Col>
              <SectionTitle>
                <SectionContent>
                  {t('l.usage.trust_score_reports.text', 'Trust Score Reports')}
                  <InfoCircleSmall onClick={openSample} style={{ cursor: 'pointer' }} />
                </SectionContent>
                <SectionContent>
                  {renderCaution()}
                  <LargeText>
                    <strong>{usedCredits}</strong>
                  </LargeText>
                  <SecondaryText>/ {totalCredits === 0 ? '--' : totalCredits}</SecondaryText>
                </SectionContent>
              </SectionTitle>
              <Row>
                <Progressbar
                  bgcolor={colors.blue40}
                  completed={getPercentage(usedCredits, totalCredits)}
                />
              </Row>
              <Indicators>
                <span>0</span>
                <span>{totalCredits === 0 ? '--' : totalCredits}</span>
              </Indicators>
            </Col>
          </Row>
        )}

        {/* <Line /> */}
        {/* Featured Listings -- To be added */}
        {/* <Row>
            <IconWrapper>
              <House width={16} height={16} />
            </IconWrapper>
            <Col>
              <SectionTitle>
                <SectionContent>
                  {t('l.usage.featured_listings.text', 'Featured Listings')}
                  <InfoCircleSmall />
                </SectionContent>
                <SectionContent>
                  <LargeText>0</LargeText>
                  <span>/ 5</span>
                </SectionContent>
              </SectionTitle>
              <Row>
                <Progressbar bgcolor={colors.blue40} completed={60} />
              </Row>
              <Indicators>
                <span>0</span>
                <span>5</span>
              </Indicators>
            </Col>
          </Row> */}
      </Content>

      <Footer>
        {planTitle === 'Enterprise' || planTitle === 'Business' ? (
          <Trans i18nKey="l.usage.modal.contact_support">
            Need more?{' '}
            <LinkText href={`${WEB_URL}/app/chat/support-room`} target="_blank" rel="noreferrer">
              Contact liv.rent support
            </LinkText>
          </Trans>
        ) : (
          <Trans i18nKey="l.usage.modal.need_more">
            Need more?{' '}
            <LinkText href={`${WEB_URL}/pricing`} target="_blank" rel="noreferrer">
              Upgrade your plan
            </LinkText>
          </Trans>
        )}
      </Footer>
    </DropdownWrapper>
  )
}

export default TsDropdown
